import { Toaster } from "react-hot-toast";
import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import "./App.css";
import { LanguageProvider } from "./contexts/languageContext";
import RootProvider from "./contexts/rootContext";
import ProtectRoute from "./middleware/protectRoute";
import UnAutherizeRoute from "./middleware/unautherizeRoute";
import MaidProtectRoute from "./middleware/maidProtectRoute";

// Regular imports - no lazy loading
const CameraDropAndGo = lazy(() => import("./components/camera/drop-and-go"));
const CameraMachine = lazy(() => import("./components/camera/machine"));

// Maid (แม่บ้าน) pages
const MaidDashboard = lazy(() => import("./pages/maid"));
const MaidLoginPage = lazy(() => import("./pages/maid/login"));
const MaidOrderDetail = lazy(() => import("./pages/maid/order"));
const MaidHistoryPage = lazy(() => import("./pages/maid/history"));
const MaidScanPage = lazy(() => import("./pages/maid/scan"));
const MaidReceiptPrintPage = lazy(() => import("./pages/maid/receipt"));
const PrinterTestPage = lazy(() => import("./pages/printer-test"));
const AboutPage = lazy(() => import("./pages/about"));
const Account = lazy(() => import("./pages/account"));
const AccountPolicy = lazy(() => import("./pages/account/policy"));
const BasketPage = lazy(() => import("./pages/basket"));
const BenefitPage = lazy(() => import("./pages/benefit"));
const GiftPage = lazy(() => import("./pages/benefit/gift"));
const GiftBasketPage = lazy(() => import("./pages/benefit/gift/basket"));
const BenefitListPage = lazy(() => import("./pages/benefit/list"));
const RedeemPage = lazy(() => import("./pages/benefit/redeem"));
const BranchDetail = lazy(() => import("./pages/branch"));
const CouponPage = lazy(() => import("./pages/coupon"));
const DeleteAccount = lazy(() => import("./pages/deleteAccount"));
const DropAndGoServiceListPage = lazy(() => import("./pages/drop-and-go/service/list"));
const BranchDetailDropAndGoMode = lazy(() => import("./pages/drop-and-go/user"));
const DropAndGoHistoryPage = lazy(() => import("./pages/drop-and-go/user/history"));
const DropAndGoOnGoaingPage = lazy(() => import("./pages/drop-and-go/user/on-going"));
const DropAndGoPayment = lazy(() => import("./pages/drop-and-go/user/payment"));
const DropAndGoReceiptPage = lazy(() => import("./pages/drop-and-go/user/receipt"));
const DropAndGoReviewPage = lazy(() => import("./pages/drop-and-go/user/review"));
const DropAndGoSummaryPage = lazy(() => import("./pages/drop-and-go/user/summary"));
const DropAndGoTosPage = lazy(() => import("./pages/drop-and-go/user/term-of-service"));
const HistoryPage = lazy(() => import("./pages/history"));
const Home = lazy(() => import("./pages/home"));
const LoginPage = lazy(() => import("./pages/login"));
const MachinePage = lazy(() => import("./pages/machine"));
const MachineDetailPage = lazy(() => import("./pages/machine/detail"));
const MachinePaymentPage = lazy(() => import("./pages/machine/payment"));
const MachinePaymentSuccessPage = lazy(() => import("./pages/machine/success"));
const MachineSummaryPage = lazy(() => import("./pages/machine/summary"));
const Map = lazy(() => import("./pages/map"));
const DropAndGoMap = lazy(() => import("./pages/map-drop-and-go"));
const NotificationPage = lazy(() => import("./pages/notification"));
const AppPolicyPage = lazy(() => import("./pages/policy"));
const AppPrivacyPage = lazy(() => import("./pages/privacy"));
const RegisterPage = lazy(() => import("./pages/register"));
const WalletPage = lazy(() => import("./pages/wallet"));
const PaymentPage = lazy(() => import("./pages/wallet/payment"));
const DropAndGoServicePaymentPage = lazy(() => import("./pages/drop-and-go/service/payment"));
const routes = ["register", "login", "/"];
console.log("version:", process.env.REACT_APP_VERSION);

function App() {
  return (
    <LanguageProvider>
      <RootProvider>
        <Toaster position="top-center" />
        <Suspense fallback={<div className="flex h-full items-center justify-center bg-white" />}>
        <Routes>
          <Route path="/">
            <Route element={<UnAutherizeRoute />}>
              <Route path="login" Component={LoginPage} />
              <Route path="register" Component={RegisterPage} />
              <Route path="policy" Component={AppPolicyPage} />
              <Route path="privacy" Component={AppPrivacyPage} />
            </Route>

            {/* Maid (แม่บ้าน) Routes */}
            <Route path="maid/login" Component={MaidLoginPage} />
            <Route path="printer-test" Component={PrinterTestPage} />
            <Route path="maid" element={<MaidProtectRoute />}>
              <Route index Component={MaidDashboard} />
              <Route path="order/:id" Component={MaidOrderDetail} />
              <Route path="history" Component={MaidHistoryPage} />
              <Route path="receipt/:orderId" Component={MaidReceiptPrintPage} />
              <Route path="scan" Component={MaidScanPage} />
              <Route path="scan/:orderId" Component={MaidScanPage} />
            </Route>
            <Route element={<ProtectRoute />}>
              <Route index element={<Home />} />
              <Route path="account">
                <Route index Component={Account} />
                <Route path="delete" Component={DeleteAccount} />
                <Route path="policy" Component={AccountPolicy} />
              </Route>

              <Route path="drop-and-go">
                <Route path="on-going" Component={DropAndGoOnGoaingPage} />
                <Route path="branch" Component={DropAndGoMap} />
                <Route path="term-of-service" Component={DropAndGoTosPage} />
                <Route path="payment" Component={DropAndGoPayment} />
                <Route path="summary" Component={DropAndGoSummaryPage} />
                <Route path="history" Component={DropAndGoHistoryPage} />
                <Route path="history/:id" Component={DropAndGoHistoryPage} />
                <Route path="review" Component={DropAndGoReviewPage} />
                <Route path="receipt" Component={DropAndGoReceiptPage} />
                <Route path="service">
                  <Route index Component={DropAndGoServiceListPage} />
                  <Route path="payment" Component={DropAndGoServicePaymentPage} />
                </Route>
              </Route>
              <Route path="map">
                <Route index Component={Map} />
                <Route path=":flag" Component={Map} />
              </Route>
              {/* <Route path="branch/:id" Component={BranchDetail} /> */}
              <Route path="branch">
                <Route path=":id" Component={BranchDetail} />
                <Route
                  path="drop-and-go/:id"
                  Component={BranchDetailDropAndGoMode}
                />
              </Route>

              <Route path="about" Component={AboutPage} />
              <Route path="notification" Component={NotificationPage} />
              <Route path="coupon" Component={CouponPage} />
              <Route path="wallet">
                <Route index Component={WalletPage} />
                <Route path=":amount" Component={PaymentPage} />
              </Route>
              <Route path="basket" Component={BasketPage} />
              <Route path="history" Component={HistoryPage} />
              <Route path="benefit">
                <Route index Component={BenefitPage} />
                <Route path="redeem" Component={RedeemPage} />
                <Route path="list" Component={BenefitListPage} />
                <Route path="gift" Component={GiftPage} />
                <Route path="gift/basket" Component={GiftBasketPage} />
              </Route>
              <Route path="machine">
                <Route path=":machineId" Component={MachinePage} />
                <Route path="detail/:machineId" Component={MachineDetailPage} />
                <Route
                  path=":machineId/summary"
                  Component={MachineSummaryPage}
                />
                <Route
                  path=":machineId/payment"
                  Component={MachinePaymentPage}
                />
                <Route
                  path=":machineId/success"
                  Component={MachinePaymentSuccessPage}
                />
              </Route>

              <Route path="camera">
                <Route path="machine" Component={CameraMachine} />
                <Route path="drop-and-go" Component={CameraDropAndGo} />
              </Route>
            </Route>
          </Route>
        </Routes>
        </Suspense>
      </RootProvider>
    </LanguageProvider>
  );
}
export default App;
